import JSZip from "jszip";
import * as pdfjsLib from "pdfjs-dist";
import * as XLSX from "xlsx";

let pdfWorkerConfigured = false;

function ensurePdfWorker() {
    if (pdfWorkerConfigured) return;
    const base = typeof process !== "undefined" && process.env && process.env.PUBLIC_URL != null ? process.env.PUBLIC_URL : "";
    const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${prefix}/pdf.worker.min.mjs`;
    pdfWorkerConfigured = true;
}

function readFileAsUtf8Text(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsText(file, "UTF-8");
    });
}

function decodeXmlEntities(str) {
    if (!str) return "";
    return str
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
        .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
}

/** Extract human-readable text from a .docx (Office Open XML) file in the browser. */
async function readDocxAsPlainText(file) {
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const entry = zip.file("word/document.xml");
    if (!entry) {
        throw new Error("Invalid DOCX (missing word/document.xml)");
    }
    const xml = await entry.async("string");
    const paragraphs = xml.split(/<\/w:p>/);
    const lines = paragraphs.map((block) => {
        const parts = [];
        const re = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
        let m;
        while ((m = re.exec(block)) !== null) {
            parts.push(decodeXmlEntities(m[1]));
        }
        return parts.join("");
    });
    let text = lines.filter((l) => l.length).join("\n").trim();
    if (!text.length) {
        text = xml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    }
    if (!text.length) {
        throw new Error("Could not extract text from this DOCX");
    }
    return text;
}

async function readPdfAsPlainText(file) {
    ensurePdfWorker();
    const data = new Uint8Array(await file.arrayBuffer());
    const pdf = await pdfjsLib.getDocument({ data, verbosity: 0 }).promise;
    const parts = [];
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const tc = await page.getTextContent();
        const line = tc.items.map((item) => ("str" in item ? item.str : "")).join(" ");
        if (line.trim()) parts.push(line.trim());
    }
    const text = parts.join("\n\n").trim();
    if (!text.length) {
        throw new Error("No extractable text found in this PDF (it may be scanned images only).");
    }
    return text;
}

async function readXlsxAsPlainText(file) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    if (!wb.SheetNames || !wb.SheetNames.length) {
        throw new Error("Empty or invalid spreadsheet.");
    }
    const chunks = [];
    for (const name of wb.SheetNames) {
        const sheet = wb.Sheets[name];
        const csv = XLSX.utils.sheet_to_csv(sheet);
        if (csv && csv.trim()) {
            chunks.push(`--- Sheet: ${name} ---\n${csv.trim()}`);
        }
    }
    const text = chunks.join("\n\n").trim();
    if (!text.length) {
        throw new Error("Could not extract text from this spreadsheet.");
    }
    return text;
}

/** PowerPoint OOXML: collect <a:t> runs from slide XML files. */
async function readPptxAsPlainText(file) {
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const slideNames = Object.keys(zip.files)
        .filter((n) => /^ppt\/slides\/slide\d+\.xml$/i.test(n))
        .sort((a, b) => {
            const na = parseInt(String(a.match(/(\d+)/)?.[1] || "0"), 10);
            const nb = parseInt(String(b.match(/(\d+)/)?.[1] || "0"), 10);
            return na - nb;
        });
    const parts = [];
    for (const n of slideNames) {
        const entry = zip.file(n);
        if (!entry) continue;
        const xml = await entry.async("string");
        const re = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g;
        let m;
        const bits = [];
        while ((m = re.exec(xml)) !== null) {
            bits.push(decodeXmlEntities(m[1]));
        }
        if (bits.length) {
            parts.push(bits.join(" "));
        }
    }
    const text = parts.join("\n\n").trim();
    if (!text.length) {
        throw new Error("Could not extract text from this PPTX.");
    }
    return text;
}

/** OpenDocument Text: content.xml inside ODF zip. */
async function readOdtAsPlainText(file) {
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const entry = zip.file("content.xml");
    if (!entry) {
        throw new Error("Invalid ODT (missing content.xml)");
    }
    const xml = await entry.async("string");
    const paras = [];
    const re = /<text:p[^>]*>([\s\S]*?)<\/text:p>/g;
    let m;
    while ((m = re.exec(xml)) !== null) {
        const inner = m[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        if (inner) paras.push(inner);
    }
    let text = paras.join("\n").trim();
    if (!text.length) {
        text = xml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    }
    if (!text.length) {
        throw new Error("Could not extract text from this ODT");
    }
    return text;
}

/** OpenDocument Spreadsheet: table cells in content.xml */
async function readOdsAsPlainText(file) {
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const entry = zip.file("content.xml");
    if (!entry) {
        throw new Error("Invalid ODS (missing content.xml)");
    }
    const xml = await entry.async("string");
    const cells = [];
    const re = /<text:p[^>]*>([\s\S]*?)<\/text:p>/g;
    let m;
    while ((m = re.exec(xml)) !== null) {
        const inner = m[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        if (inner) cells.push(inner);
    }
    const text = cells.join("\n").trim();
    if (!text.length) {
        throw new Error("Could not extract text from this ODS");
    }
    return text;
}

function looksLikeBinaryGarbage(s) {
    if (!s || s.length < 32) return false;
    let ctrl = 0;
    for (let i = 0; i < Math.min(s.length, 4000); i++) {
        const c = s.charCodeAt(i);
        if (c < 9 || (c > 13 && c < 32)) ctrl++;
    }
    return ctrl / Math.min(s.length, 4000) > 0.02;
}

/**
 * Read file as plain text for chat attachments: structured office formats and UTF-8 text.
 * Binary-only types (e.g. images, legacy .doc) return a clear error.
 */
export async function readAttachmentAsText(file) {
    const name = (file.name || "").toLowerCase();

    if (name.endsWith(".pdf")) {
        return readPdfAsPlainText(file);
    }
    if (name.endsWith(".docx")) {
        return readDocxAsPlainText(file);
    }
    if (name.endsWith(".pptx")) {
        return readPptxAsPlainText(file);
    }
    if (name.endsWith(".odt")) {
        return readOdtAsPlainText(file);
    }
    if (name.endsWith(".ods")) {
        return readOdsAsPlainText(file);
    }
    if (name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xlsb")) {
        return readXlsxAsPlainText(file);
    }
    if (name.endsWith(".xls")) {
        return readXlsxAsPlainText(file);
    }

    const imageExt = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".tif", ".tiff", ".ico", ".heic"];
    if (imageExt.some((ext) => name.endsWith(ext))) {
        throw new Error(
            "Image files do not contain selectable text in the browser. Paste the text, or attach a PDF or document export."
        );
    }

    if (name.endsWith(".doc")) {
        throw new Error(
            'Legacy .doc files are not extracted here. Save as .docx or PDF, or paste the text into the message.'
        );
    }

    const text = await readFileAsUtf8Text(file);
    if (looksLikeBinaryGarbage(text)) {
        throw new Error(
            "This file does not look like plain text. Try PDF, DOCX, XLSX, TXT, or paste the text into the message."
        );
    }
    return text;
}
